import { AppConfig, usePermissions } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Text,
  styled,
  Stack,
  useMediaQuery,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { OnSuccessCallback, SearchData } from '@ltvco/refresh-lib/v1';
import { NotificationBell, ChatAICTAButton } from '@ltvco/refresh-lib/v2';
import { Search, Menu } from '@mui/icons-material';
import { useContext, useState, type MouseEvent } from 'react';

import { HeaderSearchDropdown } from 'components/ClassicHeader/ClassicHeaderContents/DesktopHeaderNav/HeaderSearchDropdown/HeaderSearchDropdown';
import { useHeaderContext } from 'context/HeaderContext';
import { useHandleSearch } from 'hooks/useHandleSearch';

import { MobileHeaderMenuDropdown } from './MobileHeaderMenuDropdown/MobileHeaderMenuDropdown';

const StyledBox = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    padding: '0 1rem',
  },
}));

const MenuButtonContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-end',
});

const StyledIconButton = styled(IconButton)({
  width: '40px',
});

const StyledButton = styled(Button)({
  height: '100%',
  width: '100%',
  padding: '10px 8px',

  '&:hover': {
    background: 'inherit',
  },
});

const ButtonContents = styled(Box)(({ theme }) => ({
  flexDirection: 'row',
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '25px',
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[300]}`,
  padding: '4px 16px',
}));

interface MobileHeaderNavProps {
  handleLogout: () => void;
  handleNotificationBellClick: (event: MouseEvent<HTMLButtonElement>) => void;
  unreadNotificationCount: number;
}

export const MobileHeaderNav = ({
  handleLogout,
  handleNotificationBellClick,
  unreadNotificationCount,
}: MobileHeaderNavProps) => {
  const { trackEventGA4 } = useContext(AppConfig);
  const { isFreePlan } = usePermissions();
  const { handleSearchSuccess } = useHandleSearch();
  const { currentDropdown, handleSetCurrentDropdown } = useHeaderContext();
  const { isMobile } = useScreenSize();
  const isVerySmall = useMediaQuery('(max-width: 344px)');
  const [showDrawer, setShowDrawer] = useState(false);

  const onSearchSuccess: OnSuccessCallback = (searchPayload: SearchData) => {
    handleSearchSuccess && handleSearchSuccess(searchPayload);
    handleSetCurrentDropdown(null);
  };

  const toggleDrawer = () => {
    setShowDrawer(!showDrawer);
  };

  const handleClose = () => {
    handleSetCurrentDropdown(null);
  };

  const toggleSearch = () => {
    trackEventGA4({
      event_name: 'header_engagement',
      type: 'nav - search',
    });
    handleSetCurrentDropdown(currentDropdown === 'search' ? null : 'search');
  };

  const openSearch = () => {
    handleSetCurrentDropdown('search');
  };

  const openMenu = () => {
    trackEventGA4({
      event_name: 'header_engagement',
      type: 'nav - menu',
    });
    toggleDrawer();
  };

  return (
    <>
      <StyledBox>
        <StyledButton
          onClick={toggleSearch}
          id="mobile-header-search-button"
          disableRipple
        >
          <ButtonContents>
            <Search sx={{ paddingRight: 0.5 }} />
            <Text>{isVerySmall ? 'Search' : 'Vehicle Search'}</Text>
          </ButtonContents>
        </StyledButton>
        <HeaderSearchDropdown
          name={'search'}
          onSearchSuccess={onSearchSuccess}
          id="search-dropdown"
          handleClose={handleClose}
          placement={'header'}
          openSearch={openSearch}
          isFreePlan={isFreePlan}
        />
      </StyledBox>
      {!isMobile && <ChatAICTAButton name="Bumper AI" />}
      <Stack direction={'row'}>
        <Button
          sx={{
            minWidth: 0,
            px: 0.5,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
          disableRipple
          onClick={handleNotificationBellClick}
        >
          <NotificationBell unreadCount={unreadNotificationCount} />
        </Button>
        <MenuButtonContainer>
          <StyledIconButton disableRipple onClick={openMenu}>
            <Menu />
          </StyledIconButton>
        </MenuButtonContainer>
      </Stack>
      <Drawer anchor={'top'} open={showDrawer} onClose={toggleDrawer}>
        <MobileHeaderMenuDropdown
          closeMobileMenu={toggleDrawer}
          handleLogout={handleLogout}
        />
      </Drawer>
    </>
  );
};
